import { H5, Section, SectionProps, Spinner } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { PersistentSection } from "in_queue/components/common/PersistentSection";
import { useStageConfig } from "in_queue/contexts/ClusterContext";
import { useCustomUpgrades } from "in_queue/contexts/CustomUpgradesContext";
import {
    useAllAvailableProjects,
    useProjectIdToClusterMapping
} from "in_queue/contexts/ProjectDataContext";
import { useCustomUpgradesStatus } from "in_queue/hooks/useCustomUpgradesStatus";
import { StageConfig } from "in_queue/types/configPerRegion";
import {
    ScenarioAssumptionsCSV,
    ScenarioAssumptionsCSVFields,
    ScenarioMetadata
} from "in_queue/types/scenarioType";
import { shouldShowCostAssumptions } from "in_queue/utils/scenarios";
import { createContext, PropsWithChildren, useContext, useState } from "react";
import { CostAssumptionsSection } from "./Assumptions/CostAssumptionsSection";
import { CustomUpgradesSection } from "./Assumptions/CustomUpgradesSection";
import { ExportAssumptionsButton } from "./Assumptions/ExportAssumptionsButton";
import { InputDataSection } from "./Assumptions/InputDataSection";
import { ProjectSizeAdjustmentsSection } from "./Assumptions/ProjectSizeAdjustmentsSections";
import { SpeculativeProjectsSection } from "./Assumptions/SpeculativeProjectsSection";
import css from "./ScenarioAssumptions.module.scss";

const ASSUMPTION_SECTION_ORDER = [
    "projectSizeAdjustments",
    "speculativeProjects",
    "costAssumptions",
    "inputData",
    "customUpgrades"
] as const;

type ActiveSection = (typeof ASSUMPTION_SECTION_ORDER)[number];

export interface AccordionContextValue {
    activeSection: ActiveSection | null;
    toggleActiveSection: (section: ActiveSection) => void;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

export const emptyScenarioAssumptionsCSV: ScenarioAssumptionsCSV[] = [
    {
        [ScenarioAssumptionsCSVFields.project_id]: "",
        [ScenarioAssumptionsCSVFields.eris_percentage_reduction]: "",
        [ScenarioAssumptionsCSVFields.nris_percentage_reduction]: ""
    }
];

export const emptyWithdrawalAssumptionsCSV = [
    {
        [ScenarioAssumptionsCSVFields.project_id]: ""
    }
];

interface ScenarioAssumptionsProps {
    scenario: ScenarioMetadata;
    title?: React.ReactNode | string;
    /**
     * Render the group inside a collapsible panel that carries the
     * title, the summary and the CSV export, with the group dropping its
     * own frame to fill it.
     */
    renderInPanel?: boolean;
    showEmptyInputData?: boolean;
    asColumn?: boolean;
}

export interface VisibleAssumptionSections {
    speculativeProjects: boolean;
    costAssumptions: boolean;
    inputData: boolean;
    customUpgrades: boolean;
}

/**
 * Which optional assumption sections a scenario renders. Project size
 * adjustments always render, so they are absent here.
 */
const visibleAssumptionSections = (
    scenario: ScenarioMetadata,
    stageConfig: StageConfig,
    customUpgrades: boolean,
    showEmptyInputData: boolean
): VisibleAssumptionSections => ({
    speculativeProjects: stageConfig.enableSpeculativeProjects,
    costAssumptions: shouldShowCostAssumptions({
        stage: scenario.stage,
        supportedCostAssumptionTypes: stageConfig.supportedCostAssumptionTypes
    }),
    inputData: Boolean(scenario.inputDataUsed) || showEmptyInputData,
    customUpgrades
});

// Secondary surface: only show when fully enabled (treat `locked` as
// hidden).
const useCustomUpgradesSectionVisible = (): boolean =>
    useCustomUpgradesStatus() === "enabled";

export const useVisibleAssumptionSections = (
    scenario: ScenarioMetadata,
    showEmptyInputData = false
): VisibleAssumptionSections => {
    const stageConfig = useStageConfig();
    const customUpgrades = useCustomUpgradesSectionVisible();
    return visibleAssumptionSections(
        scenario,
        stageConfig,
        customUpgrades,
        showEmptyInputData
    );
};

const ScenarioAssumptionsBody: React.FC<ScenarioAssumptionsProps> = ({
    scenario,
    title,
    renderInPanel = false,
    showEmptyInputData = false,
    asColumn = false
}) => {
    const availableProjects = useAllAvailableProjects();
    const projectIdToCluster = useProjectIdToClusterMapping();
    const allCustomUpgrades = useCustomUpgrades();
    const sections = useVisibleAssumptionSections(scenario, showEmptyInputData);

    const groupClassName = [
        css["scenario-assumptions-group"],
        asColumn && css["scenario-assumptions-group-subgrid"],
        renderInPanel && css["scenario-assumptions-group-in-panel"]
    ]
        .filter(Boolean)
        .join(" ");

    if (
        projectIdToCluster === "loading" ||
        availableProjects === "loading" ||
        allCustomUpgrades === "loading"
    ) {
        // Keep the column wrapper while loading so a still-loading column
        // doesn't fall out of the shared grid and break alignment.
        return asColumn ? (
            <div className={groupClassName}>
                <Spinner />
            </div>
        ) : (
            <Spinner />
        );
    }

    return (
        <div className={groupClassName}>
            {!renderInPanel && (
                <H5 className={css["scenario-assumptions-title"]}>
                    {title || "Assumptions"}
                </H5>
            )}
            <ProjectSizeAdjustmentsSection
                scenario={scenario}
                availableProjects={availableProjects}
                projectIdToCluster={projectIdToCluster}
                showExport={!renderInPanel}
            />
            {sections.speculativeProjects && (
                <SpeculativeProjectsSection scenario={scenario} />
            )}
            {sections.costAssumptions && (
                <CostAssumptionsSection scenario={scenario} />
            )}
            {sections.inputData && <InputDataSection scenario={scenario} />}
            {sections.customUpgrades && (
                <CustomUpgradesSection
                    scenario={scenario}
                    allCustomUpgrades={allCustomUpgrades}
                />
            )}
        </div>
    );
};

const PANEL_TITLE = "Assumptions";
const PANEL_OPEN_STORAGE_KEY = "iqScenarioAssumptionsOpen";
const GRID_PANEL_OPEN_STORAGE_KEY = "iqCompareScenarioAssumptionsOpen";

/** Name the sections the group holds, in the order they render. */
const summarize = (sections: VisibleAssumptionSections): string => {
    const names = ["Project sizes"];
    if (sections.speculativeProjects) names.push("study projects");
    if (sections.costAssumptions) names.push("cost assumptions");
    if (sections.inputData) names.push("input data");
    if (sections.customUpgrades) names.push("custom upgrades");
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

/**
 * Sections any of the scenarios renders, so the summary names every row
 * the grid can show.
 */
const unionVisibleAssumptionSections = (
    all: ReadonlyArray<VisibleAssumptionSections>
): VisibleAssumptionSections => ({
    speculativeProjects: all.some((s) => s.speculativeProjects),
    costAssumptions: all.some((s) => s.costAssumptions),
    inputData: all.some((s) => s.inputData),
    customUpgrades: all.some((s) => s.customUpgrades)
});

export const ScenarioAssumptions: React.FC<ScenarioAssumptionsProps> = ({
    scenario,
    title,
    renderInPanel = false,
    showEmptyInputData = false,
    asColumn = false
}) => {
    const sections = useVisibleAssumptionSections(scenario, showEmptyInputData);
    const group = (
        <ScenarioAssumptionsProvider>
            <ScenarioAssumptionsBody
                scenario={scenario}
                title={title}
                renderInPanel={renderInPanel}
                showEmptyInputData={showEmptyInputData}
                asColumn={asColumn}
            />
        </ScenarioAssumptionsProvider>
    );

    if (!renderInPanel) return group;

    return (
        <PersistentSection
            title={PANEL_TITLE}
            summary={summarize(sections)}
            icon={IconNames.PROPERTIES}
            storageKey={PANEL_OPEN_STORAGE_KEY}
            defaultIsOpen
            actions={<ExportAssumptionsButton scenario={scenario} />}
            padded={false}
        >
            {group}
        </PersistentSection>
    );
};

export const ScenarioAssumptionsSection: React.FC<
    SectionProps & { section: ActiveSection }
> = ({ title, children, section, ...props }) => {
    const context = useContext(AccordionContext);

    if (!context) {
        return (
            <Section
                collapsible={true}
                collapseProps={{ defaultIsOpen: false }}
                compact={true}
                title={title}
                className={css["scenario-assumptions"]}
                {...props}
            >
                <div className={css["scenario-assumptions-body"]}>
                    {children}
                </div>
            </Section>
        );
    }

    const isActive = context.activeSection === section;

    return (
        <Section
            collapsible={true}
            collapseProps={{
                isOpen: context.activeSection === section,
                onToggle: () => context.toggleActiveSection(section)
            }}
            compact={true}
            title={title}
            className={[
                css["scenario-assumptions"],
                isActive && css["scenario-assumptions-active"]
            ]
                .filter(Boolean)
                .join(" ")}
            {...props}
        >
            <div className={css["scenario-assumptions-body"]}>{children}</div>
        </Section>
    );
};

const ScenarioAssumptionsProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const [activeSection, setActiveSection] = useState<ActiveSection | null>(
        null
    );

    const toggleActiveSection = (section: ActiveSection) => {
        setActiveSection((prev) => (prev === section ? null : section));
    };

    return (
        <AccordionContext.Provider
            value={{ activeSection, toggleActiveSection }}
        >
            {children}
        </AccordionContext.Provider>
    );
};

// Renders multiple scenarios' assumptions side by side in a single grid
export const ScenarioAssumptionsGrid: React.FC<{
    scenarios: ReadonlyArray<{
        scenario: ScenarioMetadata;
        title: React.ReactNode;
    }>;
    showEmptyInputData?: boolean;
    /**
     * Render the grid inside a collapsible panel that carries the title
     * and the summary.
     */
    renderInPanel?: boolean;
}> = ({ scenarios, showEmptyInputData = false, renderInPanel = false }) => {
    const stageConfig = useStageConfig();
    const customUpgrades = useCustomUpgradesSectionVisible();
    const gridClassName = [
        css["scenario-assumptions-grid"],
        renderInPanel && css["scenario-assumptions-grid-in-panel"]
    ]
        .filter(Boolean)
        .join(" ");
    const grid = (
        <div
            className={gridClassName}
            style={
                {
                    gridTemplateColumns: `repeat(${scenarios.length}, minmax(0, 1fr))`,
                    // title row + one row per section, derived from the
                    // section list so it stays in sync automatically.
                    "--assumption-row-count":
                        ASSUMPTION_SECTION_ORDER.length + 1
                } as React.CSSProperties
            }
        >
            <ScenarioAssumptionsProvider>
                {scenarios.map(({ scenario, title }) => (
                    <ScenarioAssumptionsBody
                        key={scenario.scenarioId}
                        scenario={scenario}
                        title={title}
                        showEmptyInputData={showEmptyInputData}
                        asColumn
                    />
                ))}
            </ScenarioAssumptionsProvider>
        </div>
    );

    if (!renderInPanel) return grid;

    const sections = unionVisibleAssumptionSections(
        scenarios.map(({ scenario }) =>
            visibleAssumptionSections(
                scenario,
                stageConfig,
                customUpgrades,
                showEmptyInputData
            )
        )
    );

    return (
        <PersistentSection
            title={PANEL_TITLE}
            summary={summarize(sections)}
            icon={IconNames.PROPERTIES}
            storageKey={GRID_PANEL_OPEN_STORAGE_KEY}
            defaultIsOpen
        >
            {grid}
        </PersistentSection>
    );
};

export default ScenarioAssumptions;
